import { Medicine } from "@/types/medicine";
import { Column } from "@/components/ui/organisms/DataTableDocument";
import {
  RouteOfAdminstrationEnum,
  TreatmentTypeEnum,
  AgeRestrictionEnum,
  MedicineCategoryEnum,
} from "@/types/medicine";

function createEnumMap<T extends Record<string, string>>(
  enumObj: T
): Record<number, string> {
  return Object.values(enumObj).reduce((acc, value) => {
    acc[Number(value)] = Object.keys(enumObj)
      .find((k) => enumObj[k] === value)!
      .replace(/_/g, " ");
    return acc;
  }, {} as Record<number, string>);
}

export const routeOfAdminstrationEnumMap = createEnumMap(
  RouteOfAdminstrationEnum
);
export const treatmentTypeEnumMap = createEnumMap(TreatmentTypeEnum);
export const ageRestrictionEnumMap = createEnumMap(AgeRestrictionEnum);
export const medicineCategoryEnumMap = createEnumMap(MedicineCategoryEnum);

export const medicinesTableConfig = {
  columns: [
    {
      key: "medicine_name" as keyof Medicine,
      label: "Medicine Name",
      sortable: true,
    },
    { key: "strength" as keyof Medicine, label: "Strength", sortable: false },
    {
      key: "price" as keyof Medicine,
      label: "Price",
      sortable: true,
      render: (value: number | undefined | null) =>
        value != null ? `₹${value.toFixed(2)}` : "₹0.00",
    },
    {
      key: "manufacturer_name" as keyof Medicine,
      label: "Manufacturer",
      sortable: true,
    },
    {
      key: "side_effect" as keyof Medicine,
      label: "Side Effects",
      sortable: false,
    },
    {
      key: "active_medicine" as keyof Medicine,
      label: "Status",
      sortable: true,
      render: (value: boolean) => (value ? "Active" : "Obsolete"),
    },
    {
      key: "route_of_adminstration" as keyof Medicine,
      label: "Route",
      sortable: true,
      render: (value: number) => routeOfAdminstrationEnumMap[value],
    },
    {
      key: "treatment_type" as keyof Medicine,
      label: "Treatment Type",
      sortable: true,
      render: (value: number | string) => treatmentTypeEnumMap[Number(value)],
    },
    {
      key: "age_restriction" as keyof Medicine,
      label: "Age Restriction",
      sortable: true,
      render: (value: number) => ageRestrictionEnumMap[value],
    },
    {
      key: "medicine_category" as keyof Medicine,
      label: "Category",
      sortable: true,
      render: (value: number) => medicineCategoryEnumMap[value],
    },
    {
      key: "chemical_composition" as keyof Medicine,
      label: "Chemical Composition",
      sortable: false,
    },
    {
      key: "additional_info" as keyof Medicine,
      label: "Additional Info",
      sortable: false,
    },
  ] as Column<Medicine>[],

  filters: [
    {
      key: "medicine_name",
      label: "Medicine Name",
      type: "text" as const,
      placeholder: "Search by medicine name...",
      default: true,
    },
    {
      key: "medicine_category",
      label: "Category",
      type: "select" as const,
      placeholder: "Select category...",
      options: Object.entries(medicineCategoryEnumMap).map(
        ([value, label]) => ({
          label,
          value: Number(value),
        })
      ),
    },
    {
      key: "treatment_type",
      label: "Treatment Type",
      type: "select" as const,
      placeholder: "Select treatment type...",
      options: Object.entries(treatmentTypeEnumMap).map(([value, label]) => ({
        label,
        value: Number(value),
      })),
    },
    {
      key: "age_restriction",
      label: "Age Restriction",
      type: "select" as const,
      placeholder: "Select Age...",
      options: Object.entries(ageRestrictionEnumMap).map(([value, label]) => ({
        label,
        value: Number(value),
      })),
    },
  ],
};

export const editableKeys: (keyof Medicine)[] = [
  "medicine_name",
  "manufacturer_name",
  "dosage",
  "price",
  "side_effect",
  "chemical_composition",
  "additional_info",
];

export const editableFields = medicinesTableConfig.columns.filter((col) =>
  editableKeys.includes(col.key)
);

export const filterKeyToConfigMap = medicinesTableConfig.filters.reduce(
  (acc, filter) => {
    acc[filter.key] = filter.type;
    return acc;
  },
  {} as Record<string, "text" | "select">
);
